import { createReducer } from '@reduxjs/toolkit';
import { WaybillDto, IWaybillDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    createNewWaybill,
    clearWaybill,
    saveWaybill,
    importWaybill,
    updateWaybillData,
    updateWaybillDescription,
    createNewEmptyWaybill,
    updateWaybillDeliveryDate,
    updateWaybillPickupDate,
    updateWaybillDriverName,
    updateWaybillDriverUniqueNumber,
    updateWaybillLicensePlateNum,
} from '../actions/waybillActions';
import {
    clearBaseEntityState,
    compareModifiedDate,
    IBaseEntityState,
    IModifiedInfo,
    SaveEntityStatus,
    setBaseEntityCurrentJson,
} from './common';
import { generateWaybillNumber } from '../../common/Extensions';

export interface WaybillFormReducerState extends IBaseEntityState {
    waybill: IWaybillDto | null;
}

function clearState(state: WaybillFormReducerState): void {
    state.waybill = null;

    clearBaseEntityState(state);
}

function getInitialState(): WaybillFormReducerState {
    const state: WaybillFormReducerState = {
        waybill: null,
        baseJsonString: null,
        currentJsonString: null,
        isChanged: undefined,
        saveStatus: SaveEntityStatus.None,
        modified: undefined,
    };

    clearState(state);

    return state;
}

function calculateCurrentJson(state: WaybillFormReducerState, setAsBaseline: boolean = false): void {
    myAssert(() => state.waybill != null, 'Need waybill to calculate');

    const jsonBaseline = JSON.stringify(state.waybill);

    setBaseEntityCurrentJson(state, jsonBaseline, setAsBaseline);
}

function calculateBaseInfo(state: WaybillFormReducerState) {
    myAssert(() => state.waybill != null, 'Need waybill to calculate');

    if (!state.waybill!.id || state.waybill!.id <= 0) {
        // no modified data should be set
    } else {
        // lets find the last modified part of waybill
        const modified: IModifiedInfo | undefined = compareModifiedDate(state.waybill, undefined);

        state.modified =
            (modified ?? state.waybill!.created)
                ? { modifiedAt: state.waybill!.created ?? new Date(), modifiedBy: state.waybill!.createdBy ?? '' }
                : undefined;
    }
}

export const waybillFormReducer = createReducer<WaybillFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(createNewEmptyWaybill, (state: WaybillFormReducerState, action: any) => {
                clearState(state);

                const newWaybill: IWaybillDto = new WaybillDto();

                newWaybill.id = 0;
                newWaybill.pickupDate = new Date();
                newWaybill.deliveryDate = new Date();
                newWaybill.waybillNumber = generateWaybillNumber();

                state.waybill = newWaybill;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(createNewWaybill, (state: WaybillFormReducerState, action: any) => {
                clearState(state);

                const payload: IWaybillDto = action.payload;

                // check waybill data
                myAssert(() => payload.id === 0, 'It is new waybill!!!');

                state.waybill = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state);
            })
            .addCase(importWaybill.fulfilled, (state: WaybillFormReducerState, action: any) => {
                clearState(state);

                const payload: IWaybillDto = action.payload;

                myAssert(() => !!payload, 'Required Data for Import');

                state.waybill = payload;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(clearWaybill, (state: WaybillFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(updateWaybillDescription, (state: WaybillFormReducerState, action: any) => {
                myAssert(() => state.waybill !== null, 'There needs to be waybill if it is being updated');

                state.waybill! = { ...state.waybill, description: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateWaybillDriverName, (state: WaybillFormReducerState, action: any) => {
                myAssert(() => state.waybill !== null, 'There needs to be waybill if it is being updated');

                state.waybill! = { ...state.waybill, driverName: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateWaybillDriverUniqueNumber, (state: WaybillFormReducerState, action: any) => {
                myAssert(() => state.waybill !== null, 'There needs to be waybill if it is being updated');

                state.waybill! = { ...state.waybill, driverUniqueNumber: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateWaybillLicensePlateNum, (state: WaybillFormReducerState, action: any) => {
                myAssert(() => state.waybill !== null, 'There needs to be waybill if it is being updated');

                state.waybill! = { ...state.waybill, vehicleLicensePlateNum: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateWaybillData, (state: WaybillFormReducerState, action: any) => {
                myAssert(() => state.waybill !== null, 'There needs to be waybill if it is being updated');

                state.waybill! = { ...state.waybill, data: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateWaybillPickupDate, (state: WaybillFormReducerState, action: any) => {
                myAssert(() => state.waybill !== null, 'There needs to be waybill if it is being updated');

                state.waybill! = { ...state.waybill, pickupDate: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(updateWaybillDeliveryDate, (state: WaybillFormReducerState, action: any) => {
                myAssert(() => state.waybill !== null, 'There needs to be waybill if it is being updated');

                state.waybill! = { ...state.waybill, deliveryDate: action.payload };
                calculateCurrentJson(state);
            })
            .addCase(saveWaybill.pending, (state: WaybillFormReducerState, action: any) => {
                myAssert(() => state.waybill !== null, 'There needs to be waybill if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveWaybill.fulfilled, (state: WaybillFormReducerState, action: any) => {
                myAssert(() => state.waybill !== null, 'There needs to be waybill if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;

                calculateBaseInfo(state);
                calculateCurrentJson(state, true);
            })
            .addCase(saveWaybill.rejected, (state: WaybillFormReducerState, action: any) => {
                myAssert(() => state.waybill !== null, 'There needs to be waybill if save is on its way');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
